import React, { useState } from "react";
import { FiClock, FiCalendar, FiList, FiCpu } from "react-icons/fi";
import { MiniBar } from "./waterHelpers";

const VIEWS = [
  { id: "today",   label: "Today",   icon: FiClock    },
  { id: "weekly",  label: "Weekly",  icon: FiList     },
  { id: "monthly", label: "Monthly", icon: FiCalendar },
];

const WmConsumptionPanel = ({ d }) => {
  const [view, setView] = useState("today");
  const c = d.consumption;

  const maxHourly  = Math.max(...c.hourlyToday.map(h => h.litres));
  const maxWeekly  = Math.max(...c.weeklyTrend.map(w => w.litres));
  const maxMonthly = Math.max(...c.monthlyComparison.map(m => m.litres));
  const maxSeasonal = Math.max(...c.seasonalTrend.map(m => m.litres));
  const maxBlock   = Math.max(...c.blockWise.map(b => b.litres));

  return (
    <div className="wm-consumption">

      {/* ── View Toggle ── */}
      <div className="wc-toggle-row">
        <div className="wc-view-toggle">
          {VIEWS.map(v => (
            <button
              key={v.id}
              className={`wc-view-btn ${view === v.id ? "wc-view-active" : ""}`}
              onClick={() => setView(v.id)}
            >
              <v.icon size={13} /> {v.label}
            </button>
          ))}
        </div>
        <div className="wc-insight-strip">
          <FiCpu size={13} color="#3b82f6" />
          <span>{d.summary.aiInsights[0]}</span>
        </div>
      </div>

      {/* ── Main Chart ── */}
      {view === "today" && (
        <div className="wc-chart-card">
          <div className="wc-chart-title"><FiClock size={14} /> Hourly Consumption – Today</div>
          <div className="wc-bar-chart wc-hourly-chart">
            {c.hourlyToday.map(h => (
              <div key={h.h} className="wc-bar-col">
                <div className="wc-bar-wrap">
                  <div className="wc-bar" style={{
                    height: `${maxHourly > 0 ? (h.litres / maxHourly) * 100 : 0}%`,
                    background: h.litres > 2000 ? "#ef4444" : h.litres > 1000 ? "#f59e0b" : "#3b82f6",
                  }} />
                </div>
                <span className="wc-bar-lbl">{h.h}</span>
                {h.litres > 0 && <span className="wc-bar-val">{h.litres}</span>}
              </div>
            ))}
          </div>
          <div className="wc-chart-note">Peak hours: {c.peakHours.join(" and ")} · Units: litres</div>
        </div>
      )}

      {view === "weekly" && (
        <div className="wc-chart-card">
          <div className="wc-chart-title"><FiList size={14} /> Weekly Trend</div>
          <div className="wc-bar-chart">
            {c.weeklyTrend.map(w => (
              <div key={w.d} className="wc-bar-col">
                <div className="wc-bar-label-top">{(w.litres/1000).toFixed(1)}k</div>
                <div className="wc-bar-wrap">
                  <div className="wc-bar" style={{ height: `${(w.litres / maxWeekly) * 100}%`, background: "#3b82f6" }} />
                </div>
                <span className="wc-bar-lbl">{w.d}</span>
                <span className="wc-bar-val">₹{(w.cost/1000).toFixed(1)}k</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "monthly" && (
        <div className="wc-chart-card">
          <div className="wc-chart-title"><FiCalendar size={14} /> Monthly Comparison</div>
          <div className="wc-monthly-list">
            {c.monthlyComparison.map((m, i) => (
              <div key={i} className="wc-monthly-row">
                <span className="wc-monthly-lbl">{m.month}</span>
                <div style={{ flex: 1 }}>
                  <MiniBar value={m.litres} max={maxMonthly} color="#3b82f6" height={18} />
                </div>
                <div className="wc-monthly-stats">
                  <span>{(m.litres/1000).toFixed(0)} KL</span>
                  <span style={{ color: "#ec4899" }}>₹{(m.cost/1000).toFixed(0)}k</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Bottom Row: Block-wise + Seasonal ── */}
      <div className="wc-bottom-row">

        <div className="wc-block-card">
          <div className="wc-chart-title">Block-wise Consumption – Today</div>
          {c.blockWise.map(b => (
            <div key={b.block} className="wc-block-row">
              <div className="wc-block-info">
                <div className="wc-block-name">Block {b.block}</div>
                <div className="wc-block-sub">{b.residents} residents · {b.perCapita} L/capita</div>
              </div>
              <div className="wc-block-bar">
                <MiniBar value={b.litres} max={maxBlock} color={b.perCapita > 90 ? "#ef4444" : "#3b82f6"} height={10} />
              </div>
              <div className="wc-block-stats">
                <span className="wc-block-kl" style={{ color: b.perCapita > 90 ? "#ef4444" : "#1e293b" }}>{(b.litres/1000).toFixed(2)} KL</span>
                <span className="wc-block-pct">{b.pct}%</span>
              </div>
            </div>
          ))}
          <div className="wc-percapita-note">
            Fleet average: {d.summary.consumptionPerCapita} L/person/day &nbsp;
            <span style={{ color: "#ef4444" }}>Block B is {Math.round((c.blockWise[1].perCapita / d.summary.consumptionPerCapita - 1) * 100)}% above average</span>
          </div>
        </div>

        <div className="wc-seasonal-card">
          <div className="wc-chart-title">Seasonal Trend (12 Months)</div>
          <div className="wc-seasonal-chart">
            {c.seasonalTrend.map(m => (
              <div key={m.month} className="wc-seasonal-col">
                <div className="wc-seasonal-wrap">
                  <div className="wc-seasonal-bar" style={{ height: `${(m.litres / maxSeasonal) * 100}%` }} />
                </div>
                <span className="wc-seasonal-lbl">{m.month}</span>
              </div>
            ))}
          </div>
          <div className="wc-seasonal-note">Unit: KL/day avg. Peak: May (summer), Low: Aug (monsoon)</div>
        </div>

      </div>
    </div>
  );
};

export default WmConsumptionPanel;
